import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useDeveloperContext } from '@/hooks/useDeveloperContext';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { format, isPast } from 'date-fns';
import { CheckCircle2, Plus, Phone, Mail, MessageSquare } from 'lucide-react';
import { DeveloperCustomersList } from '@/components/dashboard/developer/DeveloperCustomersList';

const STAGES = ['new', 'contacted', 'qualified', 'visit_scheduled', 'booked', 'won', 'lost'] as const;
const STAGE_LABEL: Record<string, string> = {
  new: 'New', contacted: 'Contacted', qualified: 'Qualified',
  visit_scheduled: 'Visit Scheduled', booked: 'Booked', won: 'Won', lost: 'Lost',
};

export default function MyCrm() {
  const { user } = useAuth();
  const { ctx } = useDeveloperContext();
  const qc = useQueryClient();
  const { toast } = useToast();

  // ── Pipeline ──
  const { data: leads = [] } = useQuery({
    queryKey: ['my-crm-leads', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from('leads')
        .select('id, name, phone, email, message, status, created_at, project_id, projects(name)')
        .eq('assigned_to', user!.id)
        .order('created_at', { ascending: false });
      return data || [];
    },
  });

  const moveStage = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase.from('leads').update({ status: status as any }).eq('id', id);
      if (error) throw error;
      // Activity
      await supabase.from('crm_activities').insert({
        developer_id: ctx.developerId, lead_id: id, actor_user_id: user!.id,
        type: 'status_change', payload: { to: status },
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-crm-leads'] }),
  });

  // ── Tasks ──
  const { data: tasks = [] } = useQuery({
    queryKey: ['my-crm-tasks', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from('crm_tasks')
        .select('id, title, notes, due_at, status, lead_id, leads(name)')
        .eq('owner_user_id', user!.id)
        .order('due_at', { ascending: true, nullsFirst: false });
      return data || [];
    },
  });

  const [taskOpen, setTaskOpen] = useState(false);
  const [newTask, setNewTask] = useState({ title: '', notes: '', due_at: '', lead_id: '' });

  const createTask = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.from('crm_tasks').insert({
        owner_user_id: user!.id,
        developer_id: ctx.developerId,
        title: newTask.title,
        notes: newTask.notes || null,
        due_at: newTask.due_at ? new Date(newTask.due_at).toISOString() : null,
        lead_id: newTask.lead_id || null,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: 'Task created' });
      setTaskOpen(false); setNewTask({ title: '', notes: '', due_at: '', lead_id: '' });
      qc.invalidateQueries({ queryKey: ['my-crm-tasks'] });
    },
    onError: (e: any) => toast({ variant: 'destructive', title: 'Failed', description: e.message }),
  });

  const completeTask = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('crm_tasks').update({ status: 'done' }).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-crm-tasks'] }),
  });

  // ── Activity ──
  const { data: activities = [] } = useQuery({
    queryKey: ['my-crm-activities', user?.id],
    enabled: !!user,
    queryFn: async () => {
      const { data } = await supabase
        .from('crm_activities')
        .select('id, type, payload, created_at, lead_id, leads(name)')
        .eq('actor_user_id', user!.id)
        .order('created_at', { ascending: false })
        .limit(100);
      return data || [];
    },
  });

  const byStage = (s: string) => leads.filter((l: any) => l.status === s);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-xl font-bold">My CRM</h2>
        <p className="text-sm text-muted-foreground">{ctx.developerName ?? 'Developer workspace'} · personal pipeline, tasks, and activity</p>
      </div>

      <Tabs defaultValue="pipeline">
        <TabsList>
          <TabsTrigger value="pipeline">Pipeline ({leads.length})</TabsTrigger>
          <TabsTrigger value="customers">Customers</TabsTrigger>
          <TabsTrigger value="tasks">Tasks ({tasks.filter((t: any) => t.status === 'open').length})</TabsTrigger>
          <TabsTrigger value="activity">Activity</TabsTrigger>
        </TabsList>

        {/* PIPELINE */}
        <TabsContent value="pipeline" className="mt-4">
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 overflow-x-auto">
            {STAGES.map(stage => (
              <div key={stage} className="bg-muted/40 rounded-lg p-2 min-h-[200px]">
                <div className="text-xs font-semibold uppercase mb-2 px-1">
                  {STAGE_LABEL[stage]} <span className="text-muted-foreground ml-1">({byStage(stage).length})</span>
                </div>
                <div className="space-y-2">
                  {byStage(stage).map((l: any) => (
                    <Card key={l.id} className="p-2 text-xs cursor-grab">
                      <div className="font-medium">{l.name}</div>
                      <div className="text-muted-foreground flex items-center gap-1 mt-0.5"><Phone className="h-3 w-3" />{l.phone}</div>
                      {l.projects && (
                        <div className="text-[10px] text-muted-foreground truncate mt-0.5">
                          {typeof l.projects.name === 'object' ? l.projects.name?.en : l.projects.name}
                        </div>
                      )}
                      <Select value={l.status} onValueChange={(v) => moveStage.mutate({ id: l.id, status: v })}>
                        <SelectTrigger className="h-6 text-[10px] mt-1.5"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {STAGES.map(s => <SelectItem key={s} value={s}>{STAGE_LABEL[s]}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* CUSTOMERS */}
        <TabsContent value="customers" className="mt-4">
          <DeveloperCustomersList scope="mine" />
        </TabsContent>

        {/* TASKS */}
        <TabsContent value="tasks" className="mt-4 space-y-4">
          <div className="flex justify-end">
            <Dialog open={taskOpen} onOpenChange={setTaskOpen}>
              <DialogTrigger asChild><Button size="sm"><Plus className="h-4 w-4 mr-1" />New Task</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>New task</DialogTitle></DialogHeader>
                <div className="space-y-3 py-2">
                  <div className="space-y-1.5"><Label>Title</Label><Input value={newTask.title} onChange={e => setNewTask({ ...newTask, title: e.target.value })} placeholder="Call back about financing" /></div>
                  <div className="space-y-1.5"><Label>Due date</Label><Input type="datetime-local" value={newTask.due_at} onChange={e => setNewTask({ ...newTask, due_at: e.target.value })} /></div>
                  <div className="space-y-1.5">
                    <Label>Linked lead (optional)</Label>
                    <Select value={newTask.lead_id || 'none'} onValueChange={(v) => setNewTask({ ...newTask, lead_id: v === 'none' ? '' : v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {leads.map((l: any) => <SelectItem key={l.id} value={l.id}>{l.name} — {l.phone}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5"><Label>Notes</Label><Textarea value={newTask.notes} onChange={e => setNewTask({ ...newTask, notes: e.target.value })} /></div>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setTaskOpen(false)}>Cancel</Button>
                  <Button onClick={() => createTask.mutate()} disabled={!newTask.title}>Create</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          {tasks.length === 0 ? (
            <Card><CardContent className="py-8 text-center text-sm text-muted-foreground">No tasks.</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {tasks.map((t: any) => {
                const overdue = t.due_at && t.status === 'open' && isPast(new Date(t.due_at));
                return (
                  <Card key={t.id}>
                    <CardContent className="p-3 flex items-start gap-3">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => completeTask.mutate(t.id)} disabled={t.status !== 'open'}>
                        <CheckCircle2 className={`h-5 w-5 ${t.status === 'done' ? 'text-green-600 fill-green-100' : 'text-muted-foreground'}`} />
                      </Button>
                      <div className="flex-1 min-w-0">
                        <div className={`font-medium text-sm ${t.status === 'done' ? 'line-through text-muted-foreground' : ''}`}>{t.title}</div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                          {t.due_at && <span className={overdue ? 'text-destructive font-medium' : ''}>Due {format(new Date(t.due_at), 'MMM d, HH:mm')}</span>}
                          {t.leads?.name && <span>· {t.leads.name}</span>}
                        </div>
                        {t.notes && <p className="text-xs text-muted-foreground mt-1">{t.notes}</p>}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* ACTIVITY */}
        <TabsContent value="activity" className="mt-4">
          <Card>
            <CardContent className="py-3">
              {activities.length === 0 ? <div className="text-sm text-muted-foreground py-4 text-center">No activity yet.</div> :
                <div className="space-y-2">
                  {activities.map((a: any) => (
                    <div key={a.id} className="flex items-start gap-3 text-sm border-b last:border-0 py-2">
                      <MessageSquare className="h-4 w-4 mt-0.5 text-muted-foreground" />
                      <div className="flex-1">
                        <div><Badge variant="outline" className="text-[10px] mr-2">{a.type}</Badge>{a.leads?.name}</div>
                        {Object.keys(a.payload || {}).length > 0 && (
                          <pre className="text-[10px] text-muted-foreground mt-1">{JSON.stringify(a.payload)}</pre>
                        )}
                      </div>
                      <span className="text-[10px] text-muted-foreground">{format(new Date(a.created_at), 'MMM d, HH:mm')}</span>
                    </div>
                  ))}
                </div>
              }
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
